import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SessionTabs } from '../components/SessionTabs.js';
import { api } from '../lib/api.js';
import { redactJson } from '../lib/phi.js';

interface CacheBrowserPageProps {
  sessionId: string;
}

const PAGE_SIZE = 25;

/**
 * "Imported Records" — browses the session's common-schema FHIR cache
 * (fhir_resource_cache): the resources the drop mapped to, their load status into
 * Cerbo, and the per-resource JSON (PHI-redacted unless revealed). This is the hub
 * the flat-file → FHIR → Cerbo path populates. (The legacy canonical/scrape view was
 * retired with the discovery path.)
 */
export function CacheBrowserPage({ sessionId }: CacheBrowserPageProps) {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [reveal, setReveal] = useState(false);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['fhir-cache-summary', sessionId],
    queryFn: () => api.getFhirCacheSummary(sessionId),
    refetchInterval: 5_000,
  });
  const { data: recs, isLoading: recsLoading } = useQuery({
    queryKey: ['fhir-cache', sessionId, selectedType, pageOffset],
    queryFn: () =>
      selectedType
        ? api.getFhirCache(sessionId, { resourceType: selectedType, limit: PAGE_SIZE, offset: pageOffset })
        : Promise.resolve(null),
    enabled: !!selectedType,
  });

  const byType = (summary?.byType ?? []).filter((t) => t.mapped + t.loaded + t.invalid + t.failed + t.skipped > 0);
  const totals = summary?.totals;
  const invalidTotal = (totals?.invalid ?? 0) + (totals?.failed ?? 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-6">
      <SessionTabs sessionId={sessionId} active="cache" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Imported Records</h1>
          <div className="text-sm text-text-dimmed font-mono mt-1">common-schema FHIR cache · session {sessionId.slice(0, 8)}</div>
        </div>
        <button
          type="button"
          onClick={() => {
            const next = !reveal;
            setReveal(next);
            void fetch(`/api/sessions/${sessionId}/phi/toggle`, {
              method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reveal: next }),
            }).catch(() => null);
          }}
          className="rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-widest"
          style={{
            background: reveal ? 'rgba(244,63,94,0.18)' : 'rgba(148,163,184,0.18)',
            color: reveal ? 'rgb(253,164,175)' : 'rgb(203,213,225)',
            border: `1px solid ${reveal ? 'rgba(244,63,94,0.55)' : 'rgba(148,163,184,0.45)'}`,
          }}
          title={reveal ? 'PHI visible — click to redact. Each toggle is audit-logged.' : 'PHI redacted — click to reveal. Each toggle is audit-logged.'}
        >
          {reveal ? '◉ PHI Visible' : '◌ PHI Hidden'}
        </button>
      </div>

      {isLoading && <div className="text-text-dimmed">Loading…</div>}

      {summary && byType.length === 0 && (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(148,163,184,0.06)', border: '1px solid var(--border)' }}>
          <div className="text-lg font-semibold" style={{ color: 'var(--text-secondary)' }}>No records mapped yet</div>
          <div className="text-sm mt-1 text-text-dimmed">Map a file to FHIR and launch an import — mapped resources appear here.</div>
        </div>
      )}

      {totals && byType.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Loaded to Cerbo" value={totals.loaded} accent="green" />
            <StatCard label="Mapped (cached)" value={totals.mapped} accent="blue" />
            <StatCard label="Invalid / failed" value={invalidTotal} accent="orange" />
            <StatCard label="Skipped (dedup)" value={totals.skipped} accent="dim" />
          </div>

          <Section title="By resource type — select to browse">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {byType.map((t) => {
                const isSel = t.resourceType === selectedType;
                return (
                  <button
                    key={t.resourceType}
                    type="button"
                    onClick={() => { setSelectedType(isSel ? null : t.resourceType); setPageOffset(0); }}
                    className="text-left rounded-xl px-3 py-2.5 transition-colors"
                    style={{
                      background: isSel ? 'rgba(56,189,248,0.16)' : 'rgba(0,0,0,0.18)',
                      border: `1px solid ${isSel ? 'rgba(56,189,248,0.5)' : 'var(--border)'}`,
                    }}
                  >
                    <div className="font-mono text-xs uppercase tracking-wide" style={{ color: 'rgb(94,234,212)' }}>{t.resourceType}</div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-bold">{t.loaded.toLocaleString()}</span>
                      <span className="text-xs text-text-dimmed">loaded</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-mono">
                      <span className="text-text-dimmed">{t.mapped.toLocaleString()} mapped</span>
                      {t.invalid > 0 && <span className="text-rose-300">{t.invalid} invalid</span>}
                      {t.failed > 0 && <span className="text-rose-300">{t.failed} failed</span>}
                      {t.skipped > 0 && <span className="text-text-dimmed">{t.skipped} skipped</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          {selectedType && (
            <Section title={`${selectedType} records`}>
              {recsLoading && <div className="text-text-dimmed text-sm">Loading…</div>}
              {recs && recs.rows.length === 0 && <div className="text-text-dimmed text-sm">No records.</div>}
              {recs && recs.rows.length > 0 && (
                <>
                  <div className="space-y-2">
                    {recs.rows.map((r) => (
                      <ResourceCard key={r.id} row={r} reveal={reveal} />
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}
                      disabled={pageOffset === 0}
                      className="rounded px-3 py-1 disabled:opacity-50"
                      style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-dimmed)', border: '1px solid rgba(148,163,184,0.3)' }}
                    >← Prev</button>
                    <span className="text-text-dimmed font-mono">
                      {pageOffset + 1}–{Math.min(pageOffset + recs.rows.length, recs.totalCount)} of {recs.totalCount.toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPageOffset(pageOffset + PAGE_SIZE)}
                      disabled={pageOffset + recs.rows.length >= recs.totalCount}
                      className="rounded px-3 py-1 disabled:opacity-50"
                      style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-dimmed)', border: '1px solid rgba(148,163,184,0.3)' }}
                    >Next →</button>
                  </div>
                </>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{title}</h2>
      {children}
    </section>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent: 'blue' | 'green' | 'orange' | 'dim' }) {
  const colors = {
    blue: { bg: 'rgba(56,189,248,0.12)', text: 'rgb(125,211,252)', border: 'rgba(56,189,248,0.4)' },
    green: { bg: 'rgba(13,207,184,0.12)', text: 'rgb(94,234,212)', border: 'rgba(13,207,184,0.4)' },
    orange: { bg: 'rgba(251,146,60,0.12)', text: 'rgb(253,186,116)', border: 'rgba(251,146,60,0.4)' },
    dim: { bg: 'rgba(148,163,184,0.10)', text: 'rgb(203,213,225)', border: 'rgba(148,163,184,0.3)' },
  }[accent];
  return (
    <div className="rounded-xl p-4" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: colors.text }}>{label}</div>
      <div className="text-3xl font-bold mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

interface ResourceRow {
  id: string;
  resourceType: string;
  fhirId: string;
  resource: Record<string, unknown>;
  mappingStatus: string;
  loadStatus: string | null;
  loadAction: string | null;
  destinationId: number | null;
  sourceRowRef: { uploadId?: string; rowNumber: number } | null;
  error: Record<string, unknown> | null;
}

function ResourceCard({ row, reveal }: { row: ResourceRow; reveal: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const invalid = row.mappingStatus !== 'mapped' || !!row.error;
  const loaded = row.loadStatus === 'loaded';
  const bg = invalid ? 'rgba(244,63,94,0.10)' : loaded ? 'rgba(13,207,184,0.08)' : 'rgba(148,163,184,0.05)';
  const border = invalid ? 'rgba(244,63,94,0.3)' : loaded ? 'rgba(13,207,184,0.28)' : 'rgba(148,163,184,0.18)';
  // A human label from the resource (name / code / date) for scanability.
  const res = row.resource as { name?: Array<{ family?: string; given?: string[] }>; code?: { text?: string }; type?: Array<{ text?: string }> };
  const label = (() => {
    const n = res.name?.[0];
    if (n) return `${n.family ?? ''}${n.given?.length ? ', ' + n.given.join(' ') : ''}`.trim();
    return res.code?.text ?? res.type?.[0]?.text ?? '';
  })();
  return (
    <div className="rounded-lg border" style={{ background: bg, borderColor: border }}>
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full p-2.5 text-left flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          {invalid ? (
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded text-rose-300" style={{ background: 'rgba(244,63,94,0.22)' }}>✗ {row.mappingStatus}</span>
          ) : (
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded text-emerald-300" style={{ background: 'rgba(13,207,184,0.22)' }}>✓ {row.resourceType}</span>
          )}
          {label && <span className="truncate font-medium">{reveal ? label : '••••••'}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-[10px] font-mono text-text-dimmed">
          {row.sourceRowRef?.rowNumber != null && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(148,163,184,0.12)' }} title="source row this record was mapped from">
              src #{row.sourceRowRef.rowNumber}
            </span>
          )}
          {loaded && row.loadAction && (
            <span className="px-1.5 py-0.5 rounded text-emerald-300" style={{ background: 'rgba(13,207,184,0.15)' }}>
              {row.loadAction}{row.destinationId ? ` #${row.destinationId}` : ''}
            </span>
          )}
          {!loaded && !invalid && <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(148,163,184,0.15)' }}>{row.loadStatus ?? 'unloaded'}</span>}
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
      </button>
      {expanded && (
        <div className="p-3 pt-0 space-y-2 text-xs">
          <pre className="font-mono whitespace-pre-wrap break-words bg-black/30 rounded p-2 max-h-96 overflow-y-auto">
            {JSON.stringify(reveal ? row.resource : redactJson(row.resource), null, 2)?.slice(0, 8000)}
          </pre>
          {row.error && (
            <div className="font-mono whitespace-pre-wrap break-words rounded p-2 text-rose-300" style={{ background: 'rgba(244,63,94,0.12)' }}>
              {JSON.stringify(row.error, null, 2)?.slice(0, 2000)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
